import { Component } from "react"
import PropTypes from "prop-types"
import { get, noop } from "lodash"
import { PERIOD } from "helpers/shapes"
import ComparisonSettings from "modules/comparison-settings"
import CompareTopicSummary from "modules/compare-topic-summary"
import ComparePerformance from "modules/compare-performance"
import CompareRisk from "modules/compare-risk"
import CompareExposure from "modules/compare-exposure"
import CompareComposition from "modules/compare-composition"
import SmartTradePanel from "modules/smart-trade-panel"
import SmartTradeHeadline from "../smart-trade-headline"
import { SECURITIES_TAKE } from "modules/smart-trade/model"
import CompareFactorSensitivity from "modules/compare-factor-sensitivity"
import { isMetricFactorSensitivity, getFactorSensitivityTypeFromMetric } from "helpers/factorSensitivity"

import "./style.scss"

export default class SmartTradeFinalize extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    modelId: PropTypes.string.isRequired,
    comparisonItems: PropTypes.array.isRequired,
    basketStartDate: PropTypes.string,
    selectedDate: PropTypes.string,
    endDate: PropTypes.string,
    comparisonTypes: PropTypes.array.isRequired,
    selectedComparisonType: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    setSelectedComparisonType: PropTypes.func.isRequired,
    period: PERIOD.isRequired,
    latestDate: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    setPeriod: PropTypes.func.isRequired,
    resetSelectedDate: PropTypes.func.isRequired,
    baseUrl: PropTypes.string.isRequired,
    setScorecardDate: PropTypes.func.isRequired,
    scorecardDate: PropTypes.string.isRequired,
    trackPanelTrigger: PropTypes.func.isRequired,
    setSelectedDate: PropTypes.func.isRequired,
    contentSets: PropTypes.array.isRequired,
    instrumentDescriptors: PropTypes.object,
    basketRules: PropTypes.object.isRequired,
    smartTradeRules: PropTypes.array,
    isSingleDate: PropTypes.bool.isRequired,
    scorecardHeadline: PropTypes.shape({
      type: PropTypes.string,
      change: PropTypes.string,
      refInstrument: PropTypes.object,
      risk: PropTypes.shape({
        refInstrument: PropTypes.number,
        smartTrade: PropTypes.number,
      }),
    }),
    smartTradePositionSet: PropTypes.object,
    securitiesCount: PropTypes.number,
    notifyUniverseSecurities: PropTypes.func.isRequired,
    smartTradeRefEquities: PropTypes.array,
    tradeSize: PropTypes.number.isRequired,
    segmentNormalization: PropTypes.object,
    instrumentPortfolio: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    instrumentPortfolioPositions: PropTypes.object,
  }

  componentDidMount() {
    const { securitiesCount, notifyUniverseSecurities } = this.props
    if (securitiesCount > SECURITIES_TAKE) {
      notifyUniverseSecurities()
    }
  }

  render() {
    const {
      comparisonTypes,
      selectedComparisonType,
      contentSets,
      instrumentDescriptors,
      baseUrl,
      setSelectedDate,
      trackPanelTrigger,
      selectedDate,
      scorecardDate,
      setScorecardDate,
      resetSelectedDate,
      setPeriod,
      latestDate,
      period,
      endDate,
      startDate,
      comparisonItems,
      basketStartDate,
      account,
      modelId,
      setSelectedComparisonType,
      basketRules,
      smartTradeRules,
      isSingleDate,
      scorecardHeadline,
      smartTradePositionSet,
      tradeSize,
      segmentNormalization,
      instrumentPortfolio,
      instrumentPortfolioPositions,
      smartTradeRefEquities,
    } = this.props

    const { id: selectedId, type } = selectedComparisonType
    const isContentSet = type === "contentset"
    let headline

    if (scorecardHeadline) {
      headline = (<SmartTradeHeadline
        change={scorecardHeadline.change}
        type={scorecardHeadline.type}
        risk={scorecardHeadline.risk}
        refInstrument={scorecardHeadline.refInstrument}
      />)
    }

    return <div className="components_smart-trade-finalize">
      <div className="section left-section">
        <div className="section-wrapper">
          <SmartTradePanel
            instrumentDescriptors={instrumentDescriptors}
            instrumentPortfolio={instrumentPortfolio}
            instrumentPortfolioPositions={instrumentPortfolioPositions}
            basketRules={basketRules}
            smartTradeRules={smartTradeRules}
            smartTradePositionSet={smartTradePositionSet}
            selectedDate={basketStartDate}
            tradeSize={tradeSize}
            basketSegmentNormalization={segmentNormalization.basket}
          />
        </div>
      </div>
      <div className="section right-section">
        <div className="section-wrapper">
          <ComparisonSettings
            account={account}
            modelId={modelId}
            comparisonItems={comparisonItems}
            earliestDate={basketStartDate}
            latestDate={latestDate}
            endDate={endDate}
            startDate={startDate}
            period={period}
            setPeriod={setPeriod}
            setSelectedComparisonType={setSelectedComparisonType}
            selectedComparisonType={selectedComparisonType}
            selectedDate={selectedId === "scorecard" ? scorecardDate : selectedDate}
            resetDate={resetSelectedDate}
            setSelectedDate={setScorecardDate}
            singleDateSelector={selectedId === "scorecard"}
            comparisonTypes={comparisonTypes}
            contentSets={contentSets}
            baseUrl={baseUrl}
            fixedItemNumber
            isASingleDate={isSingleDate}
            headline={headline}
          />
          {selectedId === "scorecard" && <CompareTopicSummary
            account={account}
            modelId={modelId}
            positionSets={comparisonItems}
            selectedDate={scorecardDate}
            fixedItemNumber
            instrumentDescriptors={instrumentDescriptors}
            instrumentPortfolio={instrumentPortfolio}
            maxNbColumns={3}
          />}
          {selectedId === "performance" && <ComparePerformance
            account={account}
            modelId={modelId}
            positionSets={comparisonItems}
            startDate={startDate}
            endDate={endDate}
            selectedDate={selectedDate}
            setDate={setSelectedDate}
            saveMetricToStore={noop}
            trackPanelTrigger={trackPanelTrigger}
            earliestDate={basketStartDate}
            fixedItemNumber
          />}
          {selectedId === "risk" && <CompareRisk
            account={account}
            modelId={modelId}
            positionSets={comparisonItems}
            startDate={startDate}
            endDate={endDate}
            selectedDate={selectedDate}
            setDate={setSelectedDate}
            saveMetricToStore={noop}
            trackPanelTrigger={trackPanelTrigger}
            fixedItemNumber
            hideGraph={isSingleDate}
          />}
          {(selectedId === "exposure" || isContentSet) && <CompareExposure
            account={account}
            modelId={modelId}
            positionSets={comparisonItems}
            startDate={startDate}
            endDate={endDate}
            selectedDate={selectedDate}
            setDate={setSelectedDate}
            saveMetricToStore={noop}
            trackPanelTrigger={trackPanelTrigger}
            contentSetId={isContentSet ? selectedId : null}
            fixedItemNumber
            hideGraph={isSingleDate}
          />}
          {selectedId === "composition" && <CompareComposition
            account={account}
            modelId={modelId}
            positionSets={comparisonItems}
            startDate={startDate}
            endDate={endDate}
            selectedDate={selectedDate}
            setDate={setSelectedDate}
            saveMetricToStore={noop}
            saveGroupToStore={noop}
            maxNbColumns={3}
            fixedItemNumber
            hideGraph={isSingleDate}
            refEquityDisplayOverride={smartTradeRefEquities}
            context={{ smartTrade: true, equity: get(instrumentPortfolio, "equity") }}
          />}
          {isMetricFactorSensitivity(selectedId) && <CompareFactorSensitivity
            factorSensitivityType={getFactorSensitivityTypeFromMetric(selectedId)}
            account={account}
            modelId={modelId}
            positionSets={comparisonItems}
            startDate={startDate}
            endDate={endDate}
            selectedDate={selectedDate}
            setDate={setSelectedDate}
            saveMetricToStore={noop}
            maxNbColumns={3}
            fixedItemNumber
            hideGraph={isSingleDate}
            trackPanelTrigger={trackPanelTrigger}
          />}
        </div>
      </div>
    </div>
  }
}
